import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { searchDeals, createDeal } from '../api/deals';
export const DealCombobox = ({ value, onChange }) => {
    const [allDeals, setAllDeals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);
    const wrapRef = useRef(null);
    // Fetch once on mount; mirrors dealroom-fe (cache-first, pageSize 1000).
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        searchDeals('')
            .then((deals) => { if (!cancelled)
            setAllDeals(deals); })
            .catch((e) => { if (!cancelled)
            setError(e.message); })
            .finally(() => { if (!cancelled)
            setLoading(false); });
        return () => { cancelled = true; };
    }, []);
    // Close on click outside.
    useEffect(() => {
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q)
            return allDeals.slice(0, 50);
        return allDeals.filter((d) => d.name?.toLowerCase().includes(q)).slice(0, 50);
    }, [allDeals, query]);
    const exactMatch = useMemo(() => allDeals.some((d) => d.name?.toLowerCase() === query.trim().toLowerCase()), [allDeals, query]);
    const showCreate = query.trim().length > 0 && !exactMatch;
    const handleSelect = (d) => {
        onChange(d);
        setQuery(d.name);
        setOpen(false);
    };
    const handleCreate = async () => {
        const name = query.trim();
        if (!name || creating)
            return;
        setCreating(true);
        setError(null);
        try {
            const d = await createDeal(name);
            setAllDeals((prev) => [d, ...prev]);
            onChange(d);
            setQuery(d.name);
            setOpen(false);
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setCreating(false);
        }
    };
    return (_jsxs("div", { ref: wrapRef, style: { position: 'relative' }, children: [_jsx("input", { placeholder: loading ? 'Loading dealrooms…' : 'Search or create a dealroom…', value: query, onChange: (e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                    if (value && e.target.value !== value.name)
                        onChange(null);
                }, onFocus: () => setOpen(true) }), open && (_jsxs("div", { style: {
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: 4,
                    marginTop: 2, maxHeight: 220, overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }, children: [filtered.length === 0 && !showCreate && (_jsx("div", { style: { padding: 8, color: '#9ca3af', fontSize: 12 }, children: "No dealrooms match." })), filtered.map((d) => (_jsx("div", { onMouseDown: (e) => { e.preventDefault(); handleSelect(d); }, style: {
                            padding: '6px 8px', cursor: 'pointer',
                            background: value?.dealID === d.dealID ? '#f3f4f6' : 'transparent',
                        }, onMouseEnter: (e) => (e.currentTarget.style.background = '#f3f4f6'), onMouseLeave: (e) => (e.currentTarget.style.background = value?.dealID === d.dealID ? '#f3f4f6' : 'transparent'), children: d.name }, d.dealID))), showCreate && (_jsx("div", { onMouseDown: (e) => { e.preventDefault(); handleCreate(); }, style: {
                            padding: '6px 8px', cursor: 'pointer', borderTop: filtered.length ? '1px solid #f3f4f6' : 'none',
                            color: '#16a34a', fontWeight: 500,
                        }, onMouseEnter: (e) => (e.currentTarget.style.background = '#f0fdf4'), onMouseLeave: (e) => (e.currentTarget.style.background = 'transparent'), children: creating ? `Creating "${query.trim()}"…` : `+ Create "${query.trim()}"` }))] })), error && _jsx("div", { className: "error", children: error })] }));
};
